import React, { useEffect, useState, useCallback, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, UserCheck, BookOpen, Package, ScanLine, Pill, Edit3, Mail, Inbox, Search, Copy, Phone, ChevronDown, ChevronUp, Download, ShieldCheck, Sparkles, RefreshCw, Mic, Square, FileText } from "lucide-react";

const TABS = [
  { id: "inbox", label: "Inbox", icon: Inbox },
  { id: "access", label: "Access", icon: UserCheck },
  { id: "worksheet", label: "Implant Worksheet", icon: FileText },
  { id: "studio", label: "Content Studio", icon: Sparkles },
  { id: "interview", label: "Interview Q&A", icon: BookOpen },
  { id: "products", label: "Products", icon: Package },
  { id: "implants", label: "Implant Refs", icon: ScanLine },
  { id: "drugs", label: "Drugs", icon: Pill },
];

function DetailBlock({ label, children }) {
  return (
    <div className="rounded-md border border-ink/10 bg-[#FAFAF9] p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">{label}</div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}

function DetailLine({ field, value, href }) {
  const display = value && String(value).trim() ? value : "—";
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-[11px] text-muted-foreground w-14 shrink-0">{field}</span>
      {href && value ? (
        <a href={href} className="text-[#F26A24] hover:underline break-all">{display}</a>
      ) : (
        <span className="text-ink break-all">{display}</span>
      )}
    </div>
  );
}

function XrayThumb({ file, testid }) {
  const kb = file.size_bytes ? `${(file.size_bytes / 1024).toFixed(0)} KB` : "";
  const ct = (file.content_type || "").split("/")[1]?.toUpperCase() || "FILE";
  return (
    <div
      className="rounded-md border border-ink/10 bg-[#FFF4EA] p-3 flex flex-col items-start gap-1"
      data-testid={testid}
      title={`${file.filename} — full file was attached to the email to ${'info@goldcoastoms.com'}`}
    >
      <div className="text-xs font-bold text-[#B4441A]">{ct}</div>
      <div className="text-sm font-medium break-all">{file.filename}</div>
      {kb && <div className="text-[11px] text-muted-foreground">{kb}</div>}
      <div className="text-[10px] text-muted-foreground mt-1">See inbox email for full file</div>
    </div>
  );
}

function CopyRow({ label, value, testid, mono = false, highlight = false }) {
  const copy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — please select and copy manually");
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="text-xs font-medium text-muted-foreground w-24 shrink-0 uppercase tracking-wide">{label}</div>
      <div
        className={`flex-1 min-w-0 px-3 py-2 rounded-md text-sm truncate ${
          highlight ? "bg-[#FDE6D8] text-ink font-semibold" : "bg-white border border-ink/10"
        } ${mono ? "font-mono" : ""}`}
        title={value}
      >
        {value || "—"}
      </div>
      <button
        type="button"
        onClick={copy}
        data-testid={testid}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-ink/15 text-xs font-medium hover:bg-bone"
      >
        <Copy size={13} /> Copy
      </button>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("inbox");

  return (
    <div className="px-8 py-10 lg:px-14 lg:py-14 max-w-7xl">
      <div className="uppercase-label">Admin</div>
      <h1 className="font-heading text-4xl font-semibold mt-2">Network Console</h1>

      <div className="flex flex-wrap gap-2 mt-8 border-b border-black/5">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 inline-flex items-center gap-2 transition-colors ${
              tab === t.id ? "border-ink text-ink" : "border-transparent text-muted-foreground hover:text-ink"
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "inbox" && <InboxPanel />}
        {tab === "access" && <AccessPanel />}
        {tab === "worksheet" && <WorksheetPanel />}
        {tab === "studio" && <ContentStudioPanel />}
        {tab === "interview" && <InterviewPanel />}
        {tab === "products" && <ProductsPanel />}
        {tab === "implants" && <ImplantsPanel />}
        {tab === "drugs" && <DrugsPanel />}
      </div>
    </div>
  );
}

// ---------- Implant Worksheet ----------
function WorksheetPanel() {
  const [rawText, setRawText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [data, setData] = useState({
    patient_name: "",
    birth_date: "",
    implants: [{ implant_site: "", manufacturer: "", size: "", date_of_surgery: "", abutment: "" }],
    rep_name: "",
    rep_phone: "",
    rep_email: "",
    date_created: "",
    created_by: "",
    note_for_patient: "",
  });

  const parse = async () => {
    if (!rawText.trim()) { toast.error("Paste the worksheet text first"); return; }
    setParsing(true);
    try {
      const r = await api.post("/admin/worksheet/extract", { text: rawText });
      const d = r.data || {};
      setData({
        patient_name: d.patient_name || "",
        birth_date: d.birth_date || "",
        implants: (d.implants && d.implants.length)
          ? d.implants
          : [{ implant_site: "", manufacturer: "", size: "", date_of_surgery: "", abutment: "" }],
        rep_name: d.rep_name || "",
        rep_phone: d.rep_phone || "",
        rep_email: d.rep_email || "",
        date_created: d.date_created || "",
        created_by: d.created_by || "",
        note_for_patient: d.note_for_patient || "",
      });
      toast.success(`Parsed ${d.implants?.length || 0} implant${(d.implants?.length || 0) === 1 ? "" : "s"} — review and download`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not parse — try pasting plainer text");
    } finally { setParsing(false); }
  };

  const updateImplant = (i, field, value) => {
    setData((d) => {
      const next = [...d.implants];
      next[i] = { ...next[i], [field]: value };
      return { ...d, implants: next };
    });
  };

  const addRow = () => setData((d) => ({
    ...d,
    implants: [...d.implants, { implant_site: "", manufacturer: "", size: "", date_of_surgery: "", abutment: "" }],
  }));

  const removeRow = (i) => setData((d) => ({ ...d, implants: d.implants.filter((_, idx) => idx !== i) }));

  const download = async () => {
    if (!data.patient_name.trim()) { toast.error("Patient name is required"); return; }
    setDownloading(true);
    try {
      const r = await api.post("/admin/worksheet/pdf", data, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (data.patient_name || "patient").replace(/[^A-Za-z0-9]+/g, "_");
      a.download = `ImplantWorksheet_${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Worksheet PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate PDF");
    } finally { setDownloading(false); }
  };

  const fieldCls = "w-full px-3 py-2 rounded-md border border-ink/15 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F26A24]/30 focus:border-[#F26A24]/40";
  const labelCls = "text-[11px] font-bold uppercase tracking-wider text-muted-foreground block mb-1";

  return (
    <div className="space-y-6" data-testid="worksheet-panel">
      <div className="rounded-lg border border-ink/10 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <div className="uppercase-label">Step 1 · Paste worksheet text</div>
            <h3 className="font-heading text-xl font-semibold mt-1">Build an Implant Worksheet PDF to attach to referrals.</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Paste the raw worksheet text (patient, implant sites, manufacturer, sizes, surgery date, rep info). AI parses it into typed fields you can edit, then renders a branded PDF matching your preferred layout.
            </p>
          </div>
        </div>
        <textarea
          value={rawText}
          onChange={(e) => setRawText(e.target.value)}
          data-testid="worksheet-raw-input"
          placeholder={`Example:\n\nPatient: Jane Smith\nDOB: 03/14/1962\n\n#9 Nobel Biocare 4.3x11.5mm placed 1/16/2025, Multi-Unit 17° abutment\n#10 Nobel Biocare 4.3x11.5mm placed 1/16/2025, Healing abutment\n\nRep: Mike Johnson, (516) 555-1212, mike@nobel.com`}
          className="w-full min-h-[160px] text-sm p-3 rounded-md border border-ink/15 bg-[#FAFAF9] focus:outline-none focus:ring-2 focus:ring-[#F26A24]/30 focus:border-[#F26A24]/40 font-mono"
        />
        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground">{rawText.length} chars</div>
          <button
            onClick={parse}
            disabled={!rawText.trim() || parsing}
            data-testid="worksheet-parse-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-ink text-white text-sm font-medium hover:bg-ink/90 disabled:opacity-50"
          >
            <Sparkles size={14} /> {parsing ? "Parsing…" : "Parse with AI"}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-ink/10 bg-white p-5">
        <div className="uppercase-label mb-4">Step 2 · Review &amp; edit</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Patient Name</label>
            <input className={fieldCls} value={data.patient_name} onChange={(e) => setData({ ...data, patient_name: e.target.value })} data-testid="worksheet-patient-name" />
          </div>
          <div>
            <label className={labelCls}>Birth Date</label>
            <input className={fieldCls} value={data.birth_date} onChange={(e) => setData({ ...data, birth_date: e.target.value })} placeholder="MM/DD/YYYY" data-testid="worksheet-dob" />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls}>Implants</label>
            <button onClick={addRow} data-testid="worksheet-add-row" className="text-xs text-[#F26A24] hover:underline inline-flex items-center gap-1">
              <Plus size={12} /> Add row
            </button>
          </div>
          <div className="space-y-3">
            {data.implants.map((row, i) => (
              <div key={i} className="rounded-md border border-ink/10 bg-[#FAFAF9] p-3" data-testid={`worksheet-implant-row-${i}`}>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                  <input className={fieldCls} placeholder="Site (e.g. #9)" value={row.implant_site} onChange={(e) => updateImplant(i, "implant_site", e.target.value)} />
                  <input className={fieldCls} placeholder="Manufacturer" value={row.manufacturer} onChange={(e) => updateImplant(i, "manufacturer", e.target.value)} />
                  <input className={fieldCls} placeholder="Size" value={row.size} onChange={(e) => updateImplant(i, "size", e.target.value)} />
                  <input className={fieldCls} placeholder="Date of surgery" value={row.date_of_surgery} onChange={(e) => updateImplant(i, "date_of_surgery", e.target.value)} />
                  <div className="flex gap-2">
                    <input className={`${fieldCls} flex-1`} placeholder="Abutment" value={row.abutment} onChange={(e) => updateImplant(i, "abutment", e.target.value)} />
                    {data.implants.length > 1 && (
                      <button onClick={() => removeRow(i)} className="px-2 rounded-md border border-ink/15 text-destructive hover:bg-destructive/10" title="Remove row">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Rep Name</label>
            <input className={fieldCls} value={data.rep_name} onChange={(e) => setData({ ...data, rep_name: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Rep Phone</label>
            <input className={fieldCls} value={data.rep_phone} onChange={(e) => setData({ ...data, rep_phone: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Rep Email</label>
            <input className={fieldCls} value={data.rep_email} onChange={(e) => setData({ ...data, rep_email: e.target.value })} />
          </div>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Date Created</label>
            <input className={fieldCls} value={data.date_created} onChange={(e) => setData({ ...data, date_created: e.target.value })} placeholder="MM/DD/YYYY" />
          </div>
          <div>
            <label className={labelCls}>Created By</label>
            <input className={fieldCls} value={data.created_by} onChange={(e) => setData({ ...data, created_by: e.target.value })} />
          </div>
        </div>

        <div className="mt-6">
          <label className={labelCls}>Note for Patient (optional)</label>
          <textarea className={fieldCls} rows={3} value={data.note_for_patient} onChange={(e) => setData({ ...data, note_for_patient: e.target.value })} />
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={download}
            disabled={downloading || !data.patient_name.trim()}
            data-testid="worksheet-download-btn"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-[#F26A24] text-white text-sm font-medium hover:bg-[#d95a16] disabled:opacity-50"
          >
            <Download size={14} /> {downloading ? "Rendering…" : "Download Worksheet PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Voice recorder for Content Studio answers ----------
function VoiceAnswerRecorder({ disabled, onTranscript, autoGenerate }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const timerRef = React.useRef(null);

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  useEffect(() => () => {
    stopTimer();
    try { mediaRef.current?.stop(); } catch {}
    mediaRef.current?.stream?.getTracks()?.forEach((t) => t.stop());
  }, []);

  const start = async () => {
    if (disabled || state !== "idle") return;
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Mic not supported in this browser"); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : (MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "");
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        stopTimer();
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) { setState("idle"); setElapsed(0); toast.error("No audio captured"); return; }
        setState("transcribing");
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || "audio/webm").includes("webm") ? "webm" : "wav";
          fd.append("audio", blob, `recording.${ext}`);
          const r = await api.post("/admin/studio/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (r.data?.transcript || "").trim();
          if (!text) { toast.error("Nothing was transcribed"); return; }
          onTranscript(text, { autoGenerate });
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Transcription failed");
        } finally {
          setState("idle"); setElapsed(0);
        }
      };
      mediaRef.current = rec;
      rec.start();
      setState("recording");
      setElapsed(0);
      const startedAt = Date.now();
      timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 200);
    } catch (e) {
      toast.error("Mic permission denied");
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    try { mediaRef.current?.stop(); } catch {}
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  if (state === "recording") {
    return (
      <button
        type="button"
        onClick={stop}
        data-testid="studio-voice-stop"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
        </span>
        <Square size={12} fill="currentColor" /> Stop · {mmss(elapsed)}
      </button>
    );
  }
  if (state === "transcribing") {
    return (
      <button
        type="button"
        disabled
        data-testid="studio-voice-transcribing"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-ink/15 text-xs font-medium text-muted-foreground"
      >
        <RefreshCw size={12} className="animate-spin" /> Transcribing…
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={start}
      disabled={disabled}
      data-testid="studio-voice-record"
      title={autoGenerate ? "Record your answer — AI will transcribe and generate hooks automatically" : "Record your answer — AI will transcribe into the textbox"}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#F26A24] text-[#F26A24] text-xs font-medium hover:bg-[#FFF4EA] disabled:opacity-50"
    >
      <Mic size={13} /> Record answer
    </button>
  );
}

// ---------- Content Studio ----------
function ContentStudioPanel() {
  const [personas, setPersonas] = useState("");
  const [editingPersonas, setEditingPersonas] = useState(false);
  const [draftPersonas, setDraftPersonas] = useState("");
  const [history, setHistory] = useState([]); // {question, answer}
  const [currentQ, setCurrentQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [hooks, setHooks] = useState([]); // {text, format, audience, angle}
  const [selectedHook, setSelectedHook] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState({ q: false, hooks: false, draft: false });

  // ---- Onboarding (discovery interview) ----
  const [profile, setProfile] = useState(null); // {ready, answers, progress, last_question, profile_summary, coverage}
  const [onbAnswer, setOnbAnswer] = useState("");
  const [onbBusy, setOnbBusy] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // ---- Camera topics (Phase 2) ----
  const [topics, setTopics] = useState([]); // {id, title, prompt, audience, angle, answered}
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [activeTopic, setActiveTopic] = useState(null); // the clicked topic card

  // Load personas + profile on mount; start onboarding if not ready; load topics if ready
  useEffect(() => {
    (async () => {
      try {
        const p = await api.get("/admin/studio/personas");
        setPersonas(p.data.personas || "");
        setDraftPersonas(p.data.personas || "");
      } catch {}
      try {
        const r = await api.get("/admin/studio/profile");
        setProfile(r.data);
        if (r.data?.ready) {
          const t = await api.get("/admin/studio/topics");
          setTopics(t.data?.topics || []);
          if (!(t.data?.topics || []).length) {
            await generateTopics();
          }
        } else if (!r.data?.last_question) {
          const s = await api.post("/admin/studio/onboarding/start");
          setProfile(s.data);
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load profile");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateTopics = async () => {
    setTopicsLoading(true);
    try {
      const r = await api.post("/admin/studio/topics/generate");
      setTopics(r.data?.topics || []);
      toast.success(`${(r.data?.topics || []).length} on-camera topics ready for you`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate topics");
    } finally { setTopicsLoading(false); }
  };

  const pickTopic = (topic) => {
    setActiveTopic(topic);
    setCurrentQ(topic.prompt);
    setAnswer("");
    setHooks([]);
    setSelectedHook(null);
    setDraft("");
    // Scroll the answer card into view
    setTimeout(() => {
      document.getElementById("studio-answer-card")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const markTopicDone = async (topicId, answered = true) => {
    try {
      const r = await api.post("/admin/studio/topics/mark", { topic_id: topicId, answered });
      setTopics(r.data?.topics || topics);
    } catch {}
  };

  const submitOnboardingAnswer = async (overrideAnswer) => {
    const ans = (overrideAnswer ?? onbAnswer).trim();
    if (!ans) { toast.error("Share your answer first"); return; }
    setOnbBusy(true);
    try {
      const r = await api.post("/admin/studio/onboarding/answer", { answer: ans });
      setProfile(r.data);
      setOnbAnswer("");
      if (r.data?.ready) {
        toast.success("I've got a clear read on you — generating your camera topics…");
        generateTopics();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not advance interview");
    } finally { setOnbBusy(false); }
  };

  const handleOnboardingVoice = (text, { autoGenerate }) => {
    const merged = onbAnswer.trim() ? `${onbAnswer.trim()}\n\n${text}` : text;
    setOnbAnswer(merged);
    toast.success(autoGenerate ? "Transcribed — sending to the interviewer…" : "Transcribed");
    if (autoGenerate) submitOnboardingAnswer(merged);
  };

  const resetProfile = async () => {
    if (!window.confirm("Reset your profile and restart the onboarding interview? Hooks and camera topics will be cleared.")) return;
    setOnbBusy(true);
    try {
      await api.post("/admin/studio/profile/reset");
      const s = await api.post("/admin/studio/onboarding/start");
      setProfile(s.data);
      setTopics([]); setActiveTopic(null);
      setHooks([]); setSelectedHook(null); setDraft(""); setAnswer(""); setCurrentQ("");
      toast.success("Starting over — tell me who you are");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset profile");
    } finally { setOnbBusy(false); }
  };

  const askNext = async (hist) => {
    setLoading((l) => ({ ...l, q: true }));
    try {
      const r = await api.post("/admin/studio/ask", { history: hist, personas });
      setCurrentQ(r.data.question);
      setAnswer("");
      setHooks([]);
      setSelectedHook(null);
      setDraft("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate question");
    } finally { setLoading((l) => ({ ...l, q: false })); }
  };

  const submitAnswer = async (overrideAnswer) => {
    const ans = (overrideAnswer ?? answer).trim();
    if (!ans) { toast.error("Write an answer first"); return; }
    setLoading((l) => ({ ...l, hooks: true }));
    try {
      const r = await api.post("/admin/studio/hooks", {
        question: currentQ, answer: ans, formats: ["Reel", "Carousel"], personas,
      });
      setHooks(r.data.hooks || []);
      setHistory((h) => [...h, { question: currentQ, answer: ans }]);
      if (activeTopic?.id) markTopicDone(activeTopic.id, true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hook generation failed");
    } finally { setLoading((l) => ({ ...l, hooks: false })); }
  };

  const handleVoiceTranscript = (text, { autoGenerate }) => {
    const merged = answer.trim() ? `${answer.trim()}\n\n${text}` : text;
    setAnswer(merged);
    toast.success(autoGenerate ? "Transcribed — generating hooks…" : "Transcribed");
    if (autoGenerate) submitAnswer(merged);
  };

  const pickHook = async (hook) => {
    setSelectedHook(hook);
    setDraft("");
    setLoading((l) => ({ ...l, draft: true }));
    try {
      const r = await api.post("/admin/studio/draft", {
        hook: hook.text, format: hook.format, answer, personas,
      });
      setDraft(r.data.content || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Draft failed");
    } finally { setLoading((l) => ({ ...l, draft: false })); }
  };

  const savePersonas = async () => {
    try {
      await api.put("/admin/studio/personas", { personas: draftPersonas });
      setPersonas(draftPersonas);
      setEditingPersonas(false);
      toast.success("Personas saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(draft); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  const angleColor = (angle) => ({
    Contrarian: "bg-rose-100 text-rose-700",
    Story: "bg-amber-100 text-amber-700",
    Shock: "bg-red-100 text-red-700",
    Educational: "bg-blue-100 text-blue-700",
    "Behind-the-scenes": "bg-purple-100 text-purple-700",
    Mistake: "bg-orange-100 text-orange-700",
    "Myth-bust": "bg-emerald-100 text-emerald-700",
  }[angle] || "bg-gray-100 text-gray-700");

  const onboarding = profile && !profile.ready;

  return (
    <div className="space-y-6">
      {onboarding && (
        <OnboardingPanel
          profile={profile}
          answer={onbAnswer}
          onAnswer={setOnbAnswer}
          onSubmit={() => submitOnboardingAnswer()}
          onVoice={handleOnboardingVoice}
          busy={onbBusy}
          onReset={resetProfile}
        />
      )}

      {!onboarding && profile?.ready && (
        <div className="rounded-lg border border-sage/30 bg-sage/5 p-4 flex items-start justify-between gap-4" data-testid="studio-profile-ready-banner">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-sage-deep">Your brand profile · locked in</div>
            <div className="text-sm text-ink/80 mt-1">
              {showProfile
                ? <span className="whitespace-pre-wrap">{profile.profile_summary}</span>
                : <span className="line-clamp-2">{profile.profile_summary}</span>}
            </div>
            {profile.coverage && (
              <div className="text-[11px] text-muted-foreground mt-2">Covers: {profile.coverage}</div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setShowProfile((s) => !s)} data-testid="studio-profile-toggle" className="text-xs text-ink/70 hover:underline">
              {showProfile ? "Collapse" : "Read full"}
            </button>
            <button onClick={resetProfile} disabled={onbBusy} data-testid="studio-profile-reset" className="text-xs text-[#F26A24] hover:underline inline-flex items-center gap-1 disabled:opacity-50">
              <RefreshCw size={11} /> Re-interview
            </button>
          </div>
        </div>
      )}

      {!onboarding && (<>
      {/* Phase 2 — Camera Topics gallery */}
      <div className="rounded-lg border border-ink/10 bg-white p-5" data-testid="studio-topics-gallery">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="uppercase-label">Step 2 · On-camera topics</div>
            <h3 className="font-heading text-xl font-semibold mt-1">Pick a prompt and film your answer.</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Each topic is tailored to your voice and goals. Choose one, record a short answer on camera (or type/voice it below), and I'll turn it into hook-ready content.
            </p>
          </div>
          <button
            onClick={generateTopics}
            disabled={topicsLoading}
            data-testid="studio-topics-regenerate"
            className="text-xs text-[#F26A24] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
            title="Generate a fresh batch of 10 topics based on your profile"
          >
            <RefreshCw size={11} className={topicsLoading ? "animate-spin" : ""} /> {topics.length ? "Regenerate 10" : "Generate topics"}
          </button>
        </div>

        {topicsLoading && !topics.length ? (
          <div className="py-10 text-center text-sm text-muted-foreground italic">Studying your profile to build fresh topics…</div>
        ) : !topics.length ? (
          <div className="py-10 text-center">
            <button onClick={generateTopics} data-testid="studio-topics-generate-cta" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-[#F26A24] text-white text-sm font-medium hover:bg-[#d95a16]">
              <Sparkles size={14} /> Generate my 10 camera topics
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {topics.map((t) => {
              const isActive = activeTopic?.id === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => pickTopic(t)}
                  data-testid={`studio-topic-${t.id}`}
                  className={`text-left p-4 rounded-md border transition-all ${
                    isActive ? "border-[#F26A24] bg-[#FFF7F0] shadow-sm"
                    : t.answered ? "border-sage/30 bg-sage/5 hover:border-sage/50"
                    : "border-ink/10 bg-white hover:border-[#F26A24]/40 hover:bg-[#FFFBF7]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="font-semibold text-sm text-ink">{t.title}</div>
                    <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                      t.audience === "patient" ? "bg-blue-100 text-blue-700"
                      : t.audience === "referring_dentist" ? "bg-amber-100 text-amber-700"
                      : "bg-purple-100 text-purple-700"
                    }`}>
                      {t.audience === "referring_dentist" ? "GP" : t.audience === "patient" ? "patient" : "both"}
                    </span>
                  </div>
                  <div className="text-sm text-ink/80 leading-snug">{t.prompt}</div>
                  {t.angle && <div className="text-[11px] text-muted-foreground mt-2 italic line-clamp-2">{t.angle}</div>}
                  {t.answered && (
                    <div className="text-[10px] font-bold uppercase tracking-wider text-sage-deep mt-2">✓ Hooks generated</div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Phase 3 — Answer the selected topic → hooks */}
      {activeTopic && (
      <div id="studio-answer-card" className="rounded-lg border border-[#F26A24]/30 bg-[#FFF7F0] p-5" data-testid="studio-question-card">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#B4441A]">Step 3 · Your answer</div>
          <button
            onClick={() => { setActiveTopic(null); setCurrentQ(""); setAnswer(""); setHooks([]); setSelectedHook(null); setDraft(""); }}
            data-testid="studio-answer-back"
            className="text-xs text-ink/60 hover:underline flex items-center gap-1"
          >
            ← Back to topics
          </button>
        </div>
        <div className="text-lg font-medium text-ink mb-4 min-h-[28px]">
          {currentQ}
        </div>
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          disabled={loading.q || loading.hooks}
          data-testid="studio-answer-input"
          placeholder="Type what you said on camera — or record below and I'll transcribe. Specifics and stories make the best hooks."
          className="w-full min-h-[140px] text-sm p-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-[#F26A24]/30 focus:border-[#F26A24]/40"
        />
        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground">{answer.length} chars</div>
          <div className="flex items-center gap-2 flex-wrap">
            <VoiceAnswerRecorder
              disabled={loading.q || loading.hooks}
              onTranscript={handleVoiceTranscript}
              autoGenerate
            />
            <button
              onClick={() => submitAnswer()}
              disabled={!answer.trim() || loading.hooks}
              data-testid="studio-submit-answer"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F26A24] text-white text-sm font-medium hover:bg-[#d95a16] disabled:opacity-50"
            >
              <Sparkles size={14} /> {loading.hooks ? "Generating hooks…" : "Generate hooks"}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Personas (collapsed to below the main flow) */}
      <details className="rounded-lg border border-ink/10 bg-white p-4" data-testid="studio-personas-details">
        <summary className="text-sm font-medium cursor-pointer select-none flex items-center gap-2">
          <Sparkles size={14} className="text-[#F26A24]" /> Audience personas (advanced)
        </summary>
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-muted-foreground">Who the content is written for. Edit if you want to shift the lens.</div>
            {!editingPersonas ? (
              <button onClick={() => setEditingPersonas(true)} data-testid="studio-personas-edit" className="text-xs text-[#F26A24] hover:underline flex items-center gap-1">
                <Edit3 size={12} /> Edit
              </button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => { setDraftPersonas(personas); setEditingPersonas(false); }} className="text-xs text-muted-foreground hover:underline">Cancel</button>
                <button onClick={savePersonas} data-testid="studio-personas-save" className="text-xs font-medium text-white bg-[#F26A24] hover:bg-[#d95a16] px-3 py-1 rounded-md">Save</button>
              </div>
            )}
          </div>
          {editingPersonas ? (
            <textarea
              value={draftPersonas}
              onChange={(e) => setDraftPersonas(e.target.value)}
              data-testid="studio-personas-input"
              className="w-full min-h-[120px] text-sm p-3 rounded-md border border-ink/15 focus:outline-none focus:ring-2 focus:ring-[#F26A24]/30 focus:border-[#F26A24]/40"
              placeholder="Describe the audiences you're creating content for."
            />
          ) : (
            <div className="text-sm text-ink/80 whitespace-pre-wrap">{personas || <span className="text-muted-foreground italic">No personas defined — click Edit to add some.</span>}</div>
          )}
        </div>
      </details>

      {/* Hooks grid */}
      {hooks.length > 0 && (
        <div className="rounded-lg border border-ink/10 bg-white p-5" data-testid="studio-hooks-grid">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold">{hooks.length} hook options — pick one to draft</div>
            <button onClick={() => submitAnswer()} className="text-xs text-[#F26A24] hover:underline flex items-center gap-1">
              <RefreshCw size={11} /> Regenerate hooks
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {hooks.map((h, i) => {
              const isSelected = selectedHook && selectedHook.text === h.text;
              return (
                <button
                  key={i}
                  onClick={() => pickHook(h)}
                  disabled={loading.draft}
                  data-testid={`studio-hook-${i}`}
                  className={`text-left p-4 rounded-lg border transition-all ${
                    isSelected ? "border-[#F26A24] bg-[#FFF4EA] shadow-sm" : "border-ink/10 hover:border-[#F26A24]/40 bg-white"
                  } ${loading.draft && !isSelected ? "opacity-50" : ""}`}
                >
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700">{h.format}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-sage/15 text-sage-deep">{h.audience}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${angleColor(h.angle)}`}>{h.angle}</span>
                  </div>
                  <div className="text-sm text-ink leading-snug">{h.text}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Draft output */}
      {(selectedHook || loading.draft) && (
        <div className="rounded-lg border border-ink/10 bg-white p-5" data-testid="studio-draft-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Draft · {selectedHook?.format}</div>
              <div className="text-sm font-medium">{selectedHook?.text}</div>
            </div>
            <div className="flex items-center gap-2">
              {draft && (
                <button onClick={copyDraft} data-testid="studio-draft-copy" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink/15 text-xs font-medium hover:bg-bone">
                  <Copy size={12} /> Copy
                </button>
              )}
              {selectedHook && (
                <button onClick={() => pickHook(selectedHook)} disabled={loading.draft} data-testid="studio-draft-regen" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink/15 text-xs font-medium hover:bg-bone disabled:opacity-50">
                  <RefreshCw size={12} /> Regenerate
                </button>
              )}
            </div>
          </div>
          <pre className="whitespace-pre-wrap text-sm font-sans text-ink/90 bg-[#FAFAF9] p-4 rounded-md border border-ink/5 min-h-[140px]">
            {loading.draft ? <span className="text-muted-foreground italic">Writing…</span> : draft}
          </pre>
        </div>
      )}

      {/* After hooks: back to topics */}
      {hooks.length > 0 && (
        <div className="flex justify-center">
          <button
            onClick={() => { setActiveTopic(null); setCurrentQ(""); setAnswer(""); setHooks([]); setSelectedHook(null); setDraft(""); document.querySelector('[data-testid=studio-topics-gallery]')?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
            data-testid="studio-pick-another-topic"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-[#F26A24] text-[#F26A24] text-sm font-medium hover:bg-[#FFF4EA]"
          >
            ← Pick another topic
          </button>
        </div>
      )}
      </>)}
    </div>
  );
}

// ---------- Onboarding (discovery interview) ----------
function OnboardingPanel({ profile, answer, onAnswer, onSubmit, onVoice, busy, onReset }) {
  const answers = profile?.answers || [];
  const progress = Math.max(5, Math.min(100, profile?.progress || 0));
  const q = profile?.last_question || "";
  return (
    <div className="space-y-5" data-testid="studio-onboarding">
      <div className="rounded-lg border border-[#F26A24]/30 bg-white p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-[#B4441A]">Step 1 · Let's get to know you</div>
            <h3 className="font-heading text-xl font-semibold mt-1">Before we write a single hook, I need to understand your voice.</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              I'll ask a handful of questions about who you are, who you're talking to, what you offer and how you talk. Answer in your own words — short or long is fine, voice recording works too. Once I feel I know you, I'll unlock the hook generator.
            </p>
          </div>
          <button
            onClick={onReset}
            disabled={busy || answers.length === 0}
            data-testid="studio-onboarding-reset"
            className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1 disabled:opacity-40"
            title="Clear my answers and start over"
          >
            <RefreshCw size={11} /> Start over
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
            <span>{answers.length} answered · {progress}% there</span>
            {profile?.coverage && <span className="truncate max-w-[60%]" title={profile.coverage}>Covered: {profile.coverage}</span>}
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#F26A24] transition-all duration-500"
              style={{ width: `${progress}%` }}
              data-testid="studio-onboarding-progress"
            />
          </div>
        </div>
      </div>

      {/* Current question */}
      <div className="rounded-lg border border-[#F26A24]/30 bg-[#FFF7F0] p-5" data-testid="studio-onboarding-question">
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#B4441A] mb-3">AI asks</div>
        <div className="text-lg font-medium text-ink mb-4 min-h-[28px]">
          {busy && !q ? <span className="text-muted-foreground italic">Thinking…</span> : q || "—"}
        </div>
        <textarea
          value={answer}
          onChange={(e) => onAnswer(e.target.value)}
          disabled={busy}
          data-testid="studio-onboarding-answer-input"
          placeholder="Answer in your own voice. Real stories, strong opinions, specifics beat polished bullet points."
          className="w-full min-h-[120px] text-sm p-3 rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-[#F26A24]/30 focus:border-[#F26A24]/40"
        />
        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
          <div className="text-xs text-muted-foreground">{answer.length} chars</div>
          <div className="flex items-center gap-2 flex-wrap">
            <VoiceAnswerRecorder disabled={busy} onTranscript={onVoice} autoGenerate />
            <button
              onClick={onSubmit}
              disabled={!answer.trim() || busy || !q}
              data-testid="studio-onboarding-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F26A24] text-white text-sm font-medium hover:bg-[#d95a16] disabled:opacity-50"
            >
              {busy ? "Listening…" : "Continue →"}
            </button>
          </div>
        </div>
      </div>

      {/* Prior answers accordion */}
      {answers.length > 0 && (
        <details className="rounded-lg border border-ink/10 bg-white p-4" data-testid="studio-onboarding-history">
          <summary className="text-sm font-medium cursor-pointer select-none">
            Review your {answers.length} prior answer{answers.length === 1 ? "" : "s"}
          </summary>
          <div className="mt-3 space-y-3">
            {answers.map((a, i) => (
              <div key={i} className="rounded-md bg-[#FAFAF9] border border-ink/5 p-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Q{i + 1}</div>
                <div className="text-sm font-medium">{a.question}</div>
                <div className="text-sm text-ink/80 mt-1 whitespace-pre-wrap">{a.answer}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ---------- Inbox (referrals / pad requests / coverage requests) ----------
function PrivacyToggle() {
  const [enabled, setEnabled] = useState(null); // null while loading
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings/email-privacy")
      .then((r) => setEnabled(!!r.data.enabled))
      .catch(() => setEnabled(false));
  }, []);
  const toggle = async () => {
    if (enabled === null || saving) return;
    const next = !enabled;
    setSaving(true);
    try {
      const r = await api.put("/admin/settings/email-privacy", { enabled: next });
      setEnabled(!!r.data.enabled);
      toast.success(next ? "Privacy mode ON — outbound emails strip PHI" : "Privacy mode OFF — full PHI included in emails");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };
  const on = enabled === true;
  return (
    <div className={`rounded-lg border p-4 flex items-start gap-3 ${on ? "bg-sage/5 border-sage/40" : "bg-white border-ink/10"}`} data-testid="privacy-toggle-card">
      <ShieldCheck size={20} className={on ? "text-sage-deep mt-0.5" : "text-muted-foreground mt-0.5"} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-semibold">Email privacy mode</div>
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
            on ? "bg-sage/25 text-sage-deep" : "bg-gray-200 text-gray-700"
          }`}>
            {enabled === null ? "…" : on ? "ON" : "OFF"}
          </span>
        </div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
          {on
            ? "Outbound doctor & patient confirmations exclude PHI and PDF attachments. Doctors get a portal notification and log in to view details."
            : "Outbound emails include patient name, DOB, clinical details, and PDF attachment. Use ON if your Google Workspace BAA is not signed."}
        </div>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={enabled === null || saving}
        aria-pressed={on}
        data-testid="privacy-toggle-switch"
        className={`shrink-0 mt-0.5 relative inline-flex items-center h-6 w-11 rounded-full transition-colors ${
          on ? "bg-sage-deep" : "bg-gray-300"
        } ${saving ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
      >
        <span
          className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
            on ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}

function InboxPanel() {
  const [data, setData] = useState({ referrals: [], pad_requests: [], coverage_requests: [] });
  const [section, setSection] = useState("referrals");
  const [query, setQuery] = useState("");
  const loadInbox = useCallback(() => {
    api.get("/admin/inbox").then(r => setData(r.data)).catch(() => toast.error("Inbox load failed"));
  }, []);
  useEffect(() => { loadInbox(); }, [loadInbox]);

  const endpointMap = {
    referrals: "/admin/referrals",
    pad_requests: "/admin/pad-requests",
    coverage_requests: "/admin/coverage-requests",
  };
  const deleteItem = async (id) => {
    if (!window.confirm("Delete this item from the inbox? This cannot be undone.")) return;
    try {
      await api.delete(`${endpointMap[section]}/${id}`);
      toast.success("Deleted");
      loadInbox();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const [list, filteredCount] = useMemo(() => {
    const raw = data[section] || [];
    if (section !== "referrals" || !query.trim()) return [raw, raw.length];
    const q = query.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, "");
    const matches = raw.filter((r) => {
      const hay = [
        r.patient_name, r.patient_email, r.patient_dob,
        r.referring_doctor, r.practice, r.referrer_email,
        r.referred_to_doctor, r.comments, r.other_reason,
        (r.evaluate_for || []).join(" "),
        (r.teeth || []).join(" "),
      ].filter(Boolean).join(" ").toLowerCase();
      if (hay.includes(q)) return true;
      // Digit-only match on phone numbers (ignoring formatting).
      if (qDigits.length >= 3) {
        const phoneDigits = ((r.patient_phone || "") + " " + (r.referrer_phone || "")).replace(/\D/g, "");
        if (phoneDigits.includes(qDigits)) return true;
      }
      return false;
    });
    return [matches, matches.length];
  }, [data, section, query]);

  return (
    <div className="space-y-5">
      <PrivacyToggle />
      <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
        {[
          { id: "referrals", label: `Referrals (${data.referrals.length})` },
          { id: "pad_requests", label: `Pad Requests (${data.pad_requests.length})` },
          { id: "coverage_requests", label: `Coverage (${data.coverage_requests.length})` },
        ].map((s) => (
          <button key={s.id} data-testid={`inbox-tab-${s.id}`} onClick={() => setSection(s.id)} className={`px-4 py-1.5 text-sm font-medium rounded-md ${section === s.id ? "bg-white shadow-sm text-[#F26A24]" : "text-gray-600"}`}>{s.label}</button>
        ))}
      </div>
      {section === "referrals" && data.referrals.length > 0 && (
        <div className="mt-4 relative max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input
            type="search"
            data-testid="referral-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search patient, phone, doctor, practice…"
            className="w-full pl-9 pr-9 py-2 text-sm rounded-md border border-ink/15 bg-white focus:outline-none focus:ring-2 focus:ring-[#F26A24]/30 focus:border-[#F26A24]/40"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="referral-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-ink text-xs px-2 py-1"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
          {query && (
            <div className="mt-1.5 text-[11px] text-muted-foreground">
              {filteredCount} of {data.referrals.length} match &ldquo;{query}&rdquo;
            </div>
          )}
        </div>
      )}
      <div className="mt-5 space-y-3">
        {list.length === 0 && (
          <div className="text-sm text-muted-foreground p-5 border border-dashed border-gray-200 rounded-md text-center">
            {section === "referrals" && query ? `No referrals match "${query}".` : "No items in this inbox yet."}
          </div>
        )}
        {list.map((item) => <InboxItem key={item.id} section={section} item={item} onDelete={() => deleteItem(item.id)} />)}
      </div>
    </div>
  );
}

function InboxItem({ section, item, onDelete }) {
  const [decideOpen, setDecideOpen] = useState(false);
  const [decision, setDecision] = useState("approve");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const isDecidedCoverage = section === "coverage_requests" && (item.status === "approved" || item.status === "rejected");
  const isPendingCoverage = section === "coverage_requests" && (item.status === "pending" || !item.status);
  const isReferral = section === "referrals";

  const decide = async () => {
    setBusy(true);
    try {
      const body = decision === "approve" ? { instructions: text } : { reason: text };
      await api.post(`/coverage/requests/${item.id}/${decision}`, body);
      toast.success(`Coverage ${decision === "approve" ? "approved" : "rejected"} — doctor emailed`);
      setDecideOpen(false);
      setText("");
      window.location.reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  // Prevent card-toggle when clicking action buttons/links inside the card.
  const stop = (e) => e.stopPropagation();

  return (
    <div
      className={`module-card p-5 bg-white ${isReferral ? "cursor-pointer hover:border-[#F26A24]/40 transition-colors" : ""}`}
      data-testid={`inbox-item-${item.id}`}
      onClick={isReferral ? () => setExpanded((v) => !v) : undefined}
      role={isReferral ? "button" : undefined}
      tabIndex={isReferral ? 0 : undefined}
      onKeyDown={isReferral ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded((v) => !v); } } : undefined}
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-medium flex items-center gap-2 flex-wrap">
          {isReferral && (
            <>
              <span>Referral · {item.patient_name}</span>
              <ChevronDown size={14} className={`text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
            </>
          )}
          {section === "pad_requests" && `Pad request · ${item.doctor_name}`}
          {section === "coverage_requests" && `Coverage · ${item.away_doctor_name}`}
          {isDecidedCoverage && (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${item.status === "approved" ? "bg-sage/20 text-sage-deep" : "bg-destructive/15 text-destructive"}`}>
              {item.status}
            </span>
          )}
          {isPendingCoverage && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">pending</span>
          )}
        </div>
        <div className="flex items-center gap-3" onClick={stop}>
          <div className="text-xs text-muted-foreground font-mono">{new Date(item.submitted_at).toLocaleString()}</div>
          {isPendingCoverage && (
            <>
              <button
                onClick={() => { setDecision("approve"); setText(""); setDecideOpen(true); }}
                data-testid={`coverage-approve-${item.id}`}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-sage-deep text-white hover:bg-sage-deep/90"
              >
                Approve
              </button>
              <button
                onClick={() => { setDecision("reject"); setText(""); setDecideOpen(true); }}
                data-testid={`coverage-reject-${item.id}`}
                className="px-3 py-1.5 rounded-md text-xs font-medium border border-destructive text-destructive hover:bg-destructive/10"
              >
                Reject
              </button>
            </>
          )}
          <button
            onClick={onDelete}
            data-testid={`inbox-delete-${item.id}`}
            title="Delete this item"
            className="p-1.5 hover:bg-destructive/10 text-destructive rounded-md"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Referral: prominent patient contact strip — always visible, one-tap to call/email */}
      {isReferral && (item.patient_phone || item.patient_email || item.patient_dob) && (
        <div className="mt-3 flex flex-wrap items-center gap-2" onClick={stop}>
          {item.patient_phone && (
            <a
              href={`tel:${item.patient_phone.replace(/[^+\d]/g, "")}`}
              data-testid={`referral-call-${item.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F26A24] text-white text-xs font-medium hover:bg-[#d95a16]"
            >
              <Phone size={12} /> Call {item.patient_phone}
            </a>
          )}
          {item.patient_email && (
            <a
              href={`mailto:${item.patient_email}`}
              data-testid={`referral-email-${item.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink/15 text-xs font-medium hover:bg-bone"
            >
              <Mail size={12} /> {item.patient_email}
            </a>
          )}
          {item.patient_dob && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFF4EA] text-[#B4441A] text-xs font-medium">
              DOB {item.patient_dob}
            </span>
          )}
        </div>
      )}

      <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
        {isReferral && (
          <>
            <div>From: {item.referring_doctor} {item.practice ? `· ${item.practice}` : ""}</div>
            {item.referrer_email && <div>Email: {item.referrer_email}</div>}
            {item.evaluate_for?.length > 0 && <div>Evaluate for: {item.evaluate_for.join(", ")}{item.other_reason ? ` — ${item.other_reason}` : ""}</div>}
            {item.teeth?.length > 0 && <div>Teeth: {item.teeth.join(", ")}</div>}
            {item.comments && <div className="mt-1 italic">&ldquo;{item.comments}&rdquo;</div>}
          </>
        )}
        {section === "pad_requests" && (
          <>
            <div>{item.practice}</div>
            <div>{item.address_line1}{item.address_line2 ? `, ${item.address_line2}` : ""}, {item.city}, {item.state} {item.zip_code}</div>
            <div>{item.phone} · {item.email}</div>
            <div>Quantity: {item.quantity}</div>
          </>
        )}
        {section === "coverage_requests" && (
          <>
            <div>{item.practice} · {item.practice_phone}</div>
            <div>Dates: {item.start_date} → {item.end_date}</div>
            <div>Contact: {item.contact_email}{item.forwarding_phone ? ` · forward ${item.forwarding_phone}` : ""}</div>
            {item.coverage_scope?.length > 0 && <div>Scope: {item.coverage_scope.join(", ")}</div>}
            {item.notes && <div className="mt-1 italic">&ldquo;{item.notes}&rdquo;</div>}
            {item.status === "approved" && item.approval_instructions && (
              <div className="mt-2 p-2 rounded-md bg-sage/10 text-xs">
                <div className="font-semibold text-sage-deep mb-0.5">Instructions sent to doctor:</div>
                <div className="whitespace-pre-wrap">{item.approval_instructions}</div>
              </div>
            )}
            {item.status === "rejected" && item.rejection_reason && (
              <div className="mt-2 p-2 rounded-md bg-destructive/5 text-xs">
                <div className="font-semibold text-destructive mb-0.5">Rejection reason sent to doctor:</div>
                <div className="whitespace-pre-wrap">{item.rejection_reason}</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Referral expanded detail — full patient/referrer info + x-ray thumbnails */}
      {isReferral && expanded && (
        <div className="mt-4 pt-4 border-t border-ink/5 space-y-4" data-testid={`referral-detail-${item.id}`} onClick={stop}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DetailBlock label="Patient">
              <DetailLine field="Name" value={item.patient_name} />
              <DetailLine field="DOB" value={item.patient_dob} />
              <DetailLine field="Phone" value={item.patient_phone} href={item.patient_phone ? `tel:${item.patient_phone.replace(/[^+\d]/g,"")}` : null} />
              <DetailLine field="Email" value={item.patient_email} href={item.patient_email ? `mailto:${item.patient_email}` : null} />
            </DetailBlock>
            <DetailBlock label="Referring Doctor">
              <DetailLine field="Name" value={item.referring_doctor} />
              <DetailLine field="Practice" value={item.practice} />
              <DetailLine field="Email" value={item.referrer_email} href={item.referrer_email ? `mailto:${item.referrer_email}` : null} />
              <DetailLine field="Phone" value={item.referrer_phone} href={item.referrer_phone ? `tel:${item.referrer_phone.replace(/[^+\d]/g,"")}` : null} />
            </DetailBlock>
          </div>
          {item.referred_to_doctor && (
            <div className="text-sm">
              <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Referred to:</span>{" "}
              <span className="font-medium">{item.referred_to_doctor}</span>
            </div>
          )}
          {item.xray_files?.length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">
                X-rays &amp; photos ({item.xray_files.length})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {item.xray_files.map((f, i) => (
                  <XrayThumb key={i} file={f} testid={`referral-xray-${item.id}-${i}`} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {decideOpen && (
        <div className="mt-4 pt-4 border-t border-ink/5" data-testid={`coverage-decide-form-${item.id}`}>
          <div className="text-sm font-medium mb-2">
            {decision === "approve" ? "Approve & send instructions to doctor" : "Reject this request"}
          </div>
          <textarea
            data-testid={`coverage-decide-text-${item.id}`}
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={decision === "approve"
              ? `Sample: During ${item.start_date}–${item.end_date}, forward all post-op, implant, and emergency calls to our office at 516-352-7888. We'll triage and respond directly.`
              : "Optional reason — shown to the doctor in the rejection email."}
            className="w-full px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-[#F26A24]"
          />
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={decide}
              disabled={busy}
              data-testid={`coverage-decide-confirm-${item.id}`}
              className={`px-4 py-2 rounded-md text-white text-sm font-medium disabled:opacity-50 ${decision === "approve" ? "bg-sage-deep hover:bg-sage-deep/90" : "bg-destructive hover:bg-destructive/90"}`}
            >
              {busy ? "Sending..." : decision === "approve" ? "Approve & email doctor" : "Reject & email doctor"}
            </button>
            <button onClick={() => setDecideOpen(false)} className="text-xs text-muted-foreground hover:text-ink">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminEnd() {
  return null;
}
const _unused = AdminEnd;

// ---------- Interview ----------
function InterviewPanel() {
  const [items, setItems] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [form, setForm] = useState({ category: "Diagnosis", question: "", answer: "", id: null });

  const load = useCallback(async () => {
    const [r1, r2] = await Promise.all([api.get("/admin/interview"), api.get("/admin/interview/suggestions")]);
    setItems(r1.data);
    setSuggestions(r2.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/admin/interview/${form.id}`, form);
      else await api.post("/admin/interview", form);
      toast.success("Saved");
      setForm({ category: "Diagnosis", question: "", answer: "", id: null });
      load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this Q&A?")) return;
    await api.delete(`/admin/interview/${id}`);
    load();
  };

  const edit = (it) => {
    setForm({ category: it.category, question: it.question, answer: it.answer, id: it.id });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applySuggestion = (s) => {
    setForm({ category: s.category, question: s.question, answer: "", id: null });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <div className="module-card p-6 bg-white">
          <div className="uppercase-label">Add reasoning to GCOMS.ai</div>
          <h3 className="font-heading text-xl font-semibold mt-1">{form.id ? "Edit Q&A" : "New Q&A"}</h3>
          <form onSubmit={submit} className="mt-5 space-y-4">
            <input
              data-testid="admin-interview-category"
              placeholder="Category (e.g. Diagnosis)"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-4 py-2.5 rounded-md border border-ink/15"
              required
            />
            <textarea
              data-testid="admin-interview-question"
              placeholder="Question"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              rows={2}
              className="w-full px-4 py-2.5 rounded-md border border-ink/15"
              required
            />
            <textarea
              data-testid="admin-interview-answer"
              placeholder="Your answer / reasoning (this seeds GCOMS.ai's knowledge)"
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              rows={6}
              className="w-full px-4 py-2.5 rounded-md border border-ink/15"
              required
            />
            <div className="flex gap-2">
              <button type="submit" data-testid="admin-interview-save" className="px-5 py-2.5 rounded-md bg-sage-deep text-white font-medium">{form.id ? "Update" : "Add"}</button>
              {form.id && <button type="button" onClick={() => setForm({ category: "Diagnosis", question: "", answer: "", id: null })} className="px-5 py-2.5 rounded-md border border-ink/15">Cancel</button>}
            </div>
          </form>
        </div>

        <div className="mt-6">
          <div className="uppercase-label">Suggested interview questions</div>
          <div className="space-y-2 mt-3">
            {suggestions.map((s, i) => (
              <button key={`${s.category}-${i}`} onClick={() => applySuggestion(s)} className="w-full text-left p-3 rounded-md border border-ink/10 bg-white hover:bg-bone text-sm">
                <span className="text-xs uppercase tracking-wider text-sage-deep mr-2">{s.category}</span>{s.question}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="uppercase-label">Saved Q&A ({items.length})</div>
        <div className="space-y-3 mt-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">No Q&A yet. Add the first one to start teaching GCOMS.ai.</div>}
          {items.map((it) => (
            <div key={it.id} className="module-card p-5 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="uppercase-label text-sage-deep">{it.category}</div>
                  <div className="font-medium mt-1">{it.question}</div>
                  <div className="text-sm text-muted-foreground mt-2 line-clamp-3 whitespace-pre-wrap">{it.answer}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => edit(it)} className="p-2 hover:bg-bone rounded-md" data-testid={`admin-interview-edit-${it.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => remove(it.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-md" data-testid={`admin-interview-delete-${it.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Access Management (replaces quarterly codes) ----------
function AccessPanel() {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | expired
  const [bulkText, setBulkText] = useState("");
  const [bulkDays, setBulkDays] = useState(180);
  const [bulkResult, setBulkResult] = useState(null);
  const [reminderStatus, setReminderStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  // Invite-by-email form
  const [invite, setInvite] = useState({ email: "", name: "", practice: "", days: 180 });
  const [inviteResult, setInviteResult] = useState(null);

  const load = useCallback(async () => {
    const [r1, r2] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/access/reminder-status"),
    ]);
    setUsers(r1.data);
    setReminderStatus(r2.data || {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const grant = async (user_id, days) => {
    setBusy(true);
    try {
      await api.post("/admin/users/grant", { user_id, days });
      toast.success(`Granted ${days} days`);
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const approveSignup = async (user_id, email) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${user_id}/approve`);
      toast.success(`${email} approved — 180 days of access granted`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Approval failed"); }
    finally { setBusy(false); }
  };

  const denySignup = async (user_id, email) => {
    if (!window.confirm(`Deny access request from ${email}? They'll see a "not approved" message when they next sign in.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user_id}/deny`);
      toast.success(`${email} denied`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Deny failed"); }
    finally { setBusy(false); }
  };

  const revoke = async (user_id) => {
    if (!window.confirm("Revoke access for this doctor? (They will appear as Expired but stay on the roster.)")) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user_id}/revoke`);
      toast.success("Access revoked");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const hardDelete = async (user_id, email) => {
    if (!window.confirm(`PERMANENTLY delete ${email} from the roster? This cannot be undone.`)) return;
    if (!window.confirm(`Are you sure? Type-confirm by clicking OK again to delete ${email}.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${user_id}`);
      toast.success(`${email} permanently deleted`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const setAdmin = async (user_id, email, makeAdmin) => {
    const msg = makeAdmin
      ? `Promote ${email} to admin? They'll be able to invite/remove doctors, manage drugs/products, see the inbox, and act on coverage requests.`
      : `Remove admin privileges from ${email}? They'll go back to being a regular doctor.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user_id}/set-admin`, { is_admin: makeAdmin });
      toast.success(makeAdmin ? `${email} is now an admin` : `${email} is no longer an admin`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update admin status");
    } finally { setBusy(false); }
  };

  const bulkGrant = async (e) => {
    e.preventDefault();
    const emails = bulkText
      .split(/[\s,;\n]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s && s.includes("@"));
    if (emails.length === 0) { toast.error("Paste at least one valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-grant", { emails, days: parseInt(bulkDays, 10) || 180 });
      setBulkResult(data);
      toast.success(`Granted to ${data.granted.length}; ${data.missing.length} not found`);
      setBulkText("");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const sendReminderNow = async () => {
    if (!window.confirm("Send the biannual access-refresh email now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/access/send-reminder");
      toast.success(data.sent ? `Reminder sent (${data.count} doctors listed)` : "Email send failed");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const inviteUser = async (e) => {
    e.preventDefault();
    if (!invite.email.includes("@")) { toast.error("Enter a valid email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/invite", {
        email: invite.email.trim().toLowerCase(),
        name: invite.name.trim(),
        practice: invite.practice.trim(),
        days: parseInt(invite.days, 10) || 180,
      });
      setInviteResult(data);
      if (data.created) {
        toast.success(data.emailed
          ? `Invite emailed to ${invite.email} — credentials also shown below to share manually`
          : `Account created — email failed; copy the link below and send it directly`);
      } else {
        toast.success(data.message || "User already existed — access extended");
      }
      setInvite({ email: "", name: "", practice: "", days: parseInt(invite.days, 10) || 180 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    } finally { setBusy(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  const filtered = users.filter((u) => {
    if (statusFilter !== "all" && u.status !== statusFilter) return false;
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (u.email || "").toLowerCase().includes(f)
      || (u.name || "").toLowerCase().includes(f)
      || (u.practice || "").toLowerCase().includes(f);
  });

  const pendingRequests = users.filter((u) => u.status === "pending_approval");

  return (
    <div className="space-y-8">
      {/* Pending signup approvals — surfaced on top so admins never miss a new request */}
      {pendingRequests.length > 0 && (
        <div className="module-card p-5 bg-[#FFF7F0] border-[#F26A24]/40" data-testid="pending-approvals-card">
          <div className="flex items-center gap-2 mb-3">
            <UserCheck size={16} className="text-[#F26A24]" />
            <div className="font-semibold">
              {pendingRequests.length} access request{pendingRequests.length === 1 ? "" : "s"} awaiting your approval
            </div>
          </div>
          <div className="space-y-2">
            {pendingRequests.map((u) => (
              <div key={u.id} className="rounded-md bg-white border border-ink/10 p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`pending-approval-${u.id}`}>
                <div className="min-w-0">
                  <div className="font-medium">{u.name || u.email}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    <span className="font-mono">{u.email}</span>
                    {u.practice && <> · {u.practice}</>}
                    {u.phone && <> · {u.phone}</>}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Requested {fmtDate(u.signup_requested_at || u.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => approveSignup(u.id, u.email)}
                    disabled={busy}
                    data-testid={`approve-signup-${u.id}`}
                    className="px-4 py-1.5 rounded-md text-xs font-medium bg-[#F26A24] text-white hover:bg-[#d95a16] disabled:opacity-50"
                  >
                    Approve · 180 d
                  </button>
                  <button
                    onClick={() => denySignup(u.id, u.email)}
                    disabled={busy}
                    data-testid={`deny-signup-${u.id}`}
                    className="px-4 py-1.5 rounded-md text-xs font-medium border border-ink/15 hover:bg-bone disabled:opacity-50"
                  >
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Reminder status banner */}
      <div className="module-card p-5 bg-white flex flex-wrap items-center justify-between gap-3" data-testid="reminder-status-card">
        <div className="text-sm">
          <div className="font-medium flex items-center gap-2"><Mail size={14} /> Biannual access-refresh email</div>
          <div className="text-xs text-muted-foreground mt-1">
            Sent automatically every 180 days to <span className="font-mono">info@goldcoastoms.com</span>.
            {reminderStatus?.last_sent_at && (
              <> Last activity: <span className="font-mono">{fmtDate(reminderStatus.last_sent_at)}</span> ({reminderStatus.last_reason || "—"}).</>
            )}
          </div>
        </div>
        <button onClick={sendReminderNow} disabled={busy} data-testid="send-reminder-button" className="px-4 py-2 rounded-md border border-ink/15 text-sm font-medium hover:bg-bone disabled:opacity-50">
          Send now
        </button>
      </div>

      {/* Invite a doctor (creates account + emails temp password) */}
      <div className="module-card p-6 bg-white" data-testid="invite-card">
        <div className="uppercase-label">Invite a new doctor</div>
        <h3 className="font-heading text-xl font-semibold mt-1">Create account + email login details</h3>
        <p className="text-sm text-muted-foreground mt-2">
          We'll generate a temporary password, email it to the doctor, and prompt them to set their own password on first login.
        </p>
        <form onSubmit={inviteUser} className="mt-5 grid gap-3 md:grid-cols-2">
          <input
            data-testid="invite-email-input"
            type="email"
            value={invite.email}
            onChange={(e) => setInvite({ ...invite, email: e.target.value })}
            placeholder="doctor@practice.com *"
            required
            className="px-4 py-2.5 rounded-md border border-ink/15 text-sm"
          />
          <input
            data-testid="invite-name-input"
            type="text"
            value={invite.name}
            onChange={(e) => setInvite({ ...invite, name: e.target.value })}
            placeholder="Doctor name (optional)"
            className="px-4 py-2.5 rounded-md border border-ink/15 text-sm"
          />
          <input
            data-testid="invite-practice-input"
            type="text"
            value={invite.practice}
            onChange={(e) => setInvite({ ...invite, practice: e.target.value })}
            placeholder="Practice (optional)"
            className="px-4 py-2.5 rounded-md border border-ink/15 text-sm md:col-span-2"
          />
          <div className="flex items-center gap-3 md:col-span-2">
            <label className="text-sm text-muted-foreground">Access for</label>
            <select
              data-testid="invite-days-select"
              value={invite.days}
              onChange={(e) => setInvite({ ...invite, days: e.target.value })}
              className="px-3 py-2 rounded-md border border-ink/15 text-sm"
            >
              <option value={90}>90 days</option>
              <option value={180}>180 days (biannual)</option>
              <option value={365}>365 days</option>
            </select>
            <button
              type="submit"
              disabled={busy}
              data-testid="invite-submit-button"
              className="ml-auto px-5 py-2.5 rounded-md bg-sage-deep text-white text-sm font-medium hover:bg-sage-deep/90 disabled:opacity-50"
            >
              {busy ? "Working..." : "Send invite"}
            </button>
          </div>
        </form>
        {inviteResult && inviteResult.created && (
          <div className="mt-5 rounded-lg border border-ink/10 bg-[#FFF9F3] p-4 space-y-3" data-testid="invite-result">
            {inviteResult.emailed ? (
              <div className="text-sage-deep font-medium text-sm">
                ✓ Invite emailed to {inviteResult.invited_email}. If it doesn&apos;t arrive within a few minutes, ask them to check spam — or share these credentials directly.
              </div>
            ) : (
              <div className="text-destructive font-medium text-sm">
                Account created but the email failed to send. Copy these credentials and share them with the doctor directly.
              </div>
            )}
            <CopyRow label="Login link" value={inviteResult.login_url} testid="invite-copy-link" />
            <CopyRow label="Email" value={inviteResult.invited_email} testid="invite-copy-email" mono />
            <CopyRow label="Temp password" value={inviteResult.temp_password_preview} testid="invite-copy-password" mono highlight />
            <div className="text-xs text-muted-foreground pt-1">
              The doctor will be prompted to set their own password on first sign-in. The login link auto-fills these fields so they can log in with one click.
            </div>
          </div>
        )}
        {inviteResult && !inviteResult.created && (
          <div className="mt-4 text-sm text-muted-foreground" data-testid="invite-result">
            {inviteResult.message}
          </div>
        )}
      </div>

      {/* Bulk grant */}
      <div className="module-card p-6 bg-white" data-testid="bulk-grant-card">
        <div className="uppercase-label">Bulk re-input referrers</div>
        <h3 className="font-heading text-xl font-semibold mt-1">Activate a list of doctors at once</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Paste a list of emails (one per line, or comma/space separated). Each will be granted (or extended by) the days you choose.
          Doctors who haven't signed up yet will be returned as "missing" so you can invite them.
        </p>
        <form onSubmit={bulkGrant} className="mt-5 space-y-3">
          <textarea
            data-testid="bulk-emails-input"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder="alice@example.com&#10;bob@example.com, carol@example.com"
            rows={4}
            className="w-full px-4 py-2.5 rounded-md border border-ink/15 font-mono text-sm"
          />
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-muted-foreground">Grant for</label>
            <select data-testid="bulk-days-select" value={bulkDays} onChange={(e) => setBulkDays(e.target.value)} className="px-3 py-2 rounded-md border border-ink/15 text-sm">
              <option value={90}>90 days</option>
              <option value={180}>180 days (biannual)</option>
              <option value={365}>365 days</option>
            </select>
            <button type="submit" disabled={busy} data-testid="bulk-grant-button" className="px-5 py-2.5 rounded-md bg-sage-deep text-white text-sm font-medium hover:bg-sage-deep/90 disabled:opacity-50">
              {busy ? "Working..." : "Grant access"}
            </button>
          </div>
        </form>
        {bulkResult && (
          <div className="mt-4 text-sm" data-testid="bulk-grant-result">
            <div className="text-sage-deep font-medium">{bulkResult.granted.length} granted</div>
            {bulkResult.missing.length > 0 && (
              <div className="mt-2">
                <div className="text-destructive font-medium">{bulkResult.missing.length} not found (need to sign up):</div>
                <div className="text-xs font-mono mt-1 text-muted-foreground">{bulkResult.missing.join(", ")}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* User list */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="uppercase-label">Doctors ({filtered.length} / {users.length})</div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
              {[
                { id: "all", label: "All" },
                { id: "pending_approval", label: "Awaiting approval" },
                { id: "active", label: "Active" },
                { id: "pending", label: "Invited" },
                { id: "expired", label: "Expired" },
              ].map((s) => (
                <button key={s.id} onClick={() => setStatusFilter(s.id)} data-testid={`access-filter-${s.id}`} className={`px-3 py-1 text-xs font-medium rounded-md ${statusFilter === s.id ? "bg-white shadow-sm text-ink" : "text-gray-600"}`}>
                  {s.label}
                </button>
              ))}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="access-search"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search name, email, practice"
                className="pl-9 pr-3 py-2 rounded-md border border-ink/15 text-sm min-w-[260px]"
              />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {filtered.length === 0 && <div className="text-sm text-muted-foreground p-5 border border-dashed border-gray-200 rounded-md text-center">No doctors match the filter.</div>}
          {filtered.map((u) => (
            <div key={u.id} className="module-card p-4 bg-white flex flex-wrap items-center justify-between gap-3" data-testid={`access-row-${u.id}`}>
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {u.name || u.email}
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                    u.status === "active" ? "bg-sage/20 text-sage-deep"
                    : u.status === "pending_approval" ? "bg-[#FDE6D8] text-[#B4441A]"
                    : u.status === "pending" ? "bg-amber-100 text-amber-700"
                    : "bg-destructive/15 text-destructive"
                  }`}>
                    {u.status === "pending_approval" ? "awaiting approval" : u.status}
                  </span>
                  {u.is_admin && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-ink text-bone">admin</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  <span className="font-mono">{u.email}</span>
                  {u.practice && <> · {u.practice}</>}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Expires: <span className="font-mono">{fmtDate(u.manual_access_expires_at)}</span>
                  {u.subscription_expires_at && <> · Sub: <span className="font-mono">{fmtDate(u.subscription_expires_at)}</span></>}
                </div>
              </div>
              {!u.is_admin && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => grant(u.id, 90)} disabled={busy} data-testid={`grant-90-${u.id}`} className="px-3 py-1.5 rounded-md text-xs font-medium border border-ink/15 hover:bg-bone disabled:opacity-50">+90 d</button>
                  <button onClick={() => grant(u.id, 180)} disabled={busy} data-testid={`grant-180-${u.id}`} className="px-3 py-1.5 rounded-md text-xs font-medium bg-sage-deep text-white hover:bg-sage-deep/90 disabled:opacity-50">+180 d</button>
                  <button onClick={() => grant(u.id, 365)} disabled={busy} data-testid={`grant-365-${u.id}`} className="px-3 py-1.5 rounded-md text-xs font-medium border border-ink/15 hover:bg-bone disabled:opacity-50">+365 d</button>
                  <button onClick={() => setAdmin(u.id, u.email, true)} disabled={busy} data-testid={`make-admin-${u.id}`} title="Promote to admin" className="px-2 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border border-ink text-ink hover:bg-ink hover:text-bone disabled:opacity-50">+Admin</button>
                  <button onClick={() => revoke(u.id)} disabled={busy} data-testid={`revoke-${u.id}`} title="Revoke (keep on roster as expired)" className="p-2 hover:bg-destructive/10 text-destructive rounded-md disabled:opacity-50"><Trash2 size={14} /></button>
                  <button onClick={() => hardDelete(u.id, u.email)} disabled={busy} data-testid={`delete-user-${u.id}`} title="Permanently delete from roster" className="px-2 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50">Delete</button>
                </div>
              )}
              {u.is_admin && u.email !== "admin@yunov.ai" && (
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => setAdmin(u.id, u.email, false)} disabled={busy} data-testid={`remove-admin-${u.id}`} title="Demote to regular doctor" className="px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border border-ink/30 text-ink/70 hover:border-ink hover:text-ink disabled:opacity-50">Remove admin</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Products ----------
function ProductsPanel() {
  const [items, setItems] = useState([]);
  const empty = { name: "", category: "", description: "", price: 0, internal_vendor_note: "", stock: 100 };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/admin/products"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) };
    try {
      if (editing) await api.put(`/admin/products/${editing}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved"); setForm(empty); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };
  const edit = (p) => { setForm(p); setEditing(p.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (!window.confirm("Remove?")) return; await api.delete(`/admin/products/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="module-card p-6 bg-white h-fit">
        <h3 className="font-heading text-xl font-semibold">{editing ? "Edit product" : "Add product"}</h3>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input data-testid="admin-product-name" placeholder="Product name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <input data-testid="admin-product-category" placeholder="Category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <textarea data-testid="admin-product-description" placeholder="Description (shown to buyers)" rows={3} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <input data-testid="admin-product-price" placeholder="Price (USD)" type="number" step="0.01" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <textarea data-testid="admin-product-vendor" placeholder="Internal vendor note (private)" rows={2} value={form.internal_vendor_note} onChange={(e) => setForm({ ...form, internal_vendor_note: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15 bg-bone" />
          <p className="text-xs text-muted-foreground">Vendor notes are never shown to buyers.</p>
          <div className="flex gap-2">
            <button type="submit" data-testid="admin-product-save" className="px-5 py-2.5 rounded-md bg-terracotta-deep text-white font-medium">{editing ? "Update" : "Add"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="px-5 py-2.5 rounded-md border border-ink/15">Cancel</button>}
          </div>
        </form>
      </div>
      <div>
        <div className="uppercase-label">Catalog ({items.length})</div>
        <div className="mt-3 space-y-2">
          {items.map((p) => (
            <div key={p.id} className="module-card p-4 bg-white flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.category} · ${p.price.toFixed(2)} · {p.active === false ? "hidden" : "active"}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => edit(p)} className="p-2 hover:bg-bone rounded-md" data-testid={`admin-product-edit-${p.id}`}><Edit3 size={14} /></button>
                <button onClick={() => remove(p.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-md" data-testid={`admin-product-delete-${p.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Implants ----------
function ImplantsPanel() {
  const [items, setItems] = useState([]);
  const empty = { name: "", manufacturer: "", description: "", parts_url: "", identifiers: "" };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/implants/reference"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/implants/${editing}`, form);
      else await api.post("/admin/implants", form);
      toast.success("Saved"); setForm(empty); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };
  const edit = (it) => { setForm(it); setEditing(it.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (!window.confirm("Remove?")) return; await api.delete(`/admin/implants/${id}`); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="module-card p-6 bg-white h-fit">
        <h3 className="font-heading text-xl font-semibold">{editing ? "Edit implant ref" : "Add implant ref"}</h3>
        <p className="text-sm text-muted-foreground mt-2">
          Curate a small list of implants you prescribe most often, with direct buy-parts URLs. Used for tracking your scan history.
        </p>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input data-testid="admin-implant-name" placeholder="Implant name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <input data-testid="admin-implant-manufacturer" placeholder="Manufacturer" required value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <textarea data-testid="admin-implant-description" placeholder="Description" rows={3} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <input data-testid="admin-implant-identifiers" placeholder="Identifiers (connection, platform, etc.)" value={form.identifiers} onChange={(e) => setForm({ ...form, identifiers: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <input data-testid="admin-implant-parts-url" placeholder="Parts URL" type="url" required value={form.parts_url} onChange={(e) => setForm({ ...form, parts_url: e.target.value })} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <div className="flex gap-2">
            <button type="submit" data-testid="admin-implant-save" className="px-5 py-2.5 rounded-md bg-sky-deep text-white font-medium">{editing ? "Update" : "Add"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="px-5 py-2.5 rounded-md border border-ink/15">Cancel</button>}
          </div>
        </form>
      </div>
      <div>
        <div className="uppercase-label">Reference library ({items.length})</div>
        <div className="mt-3 space-y-2">
          {items.map((p) => (
            <div key={p.id} className="module-card p-4 bg-white flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.manufacturer}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => edit(p)} className="p-2 hover:bg-bone rounded-md" data-testid={`admin-implant-edit-${p.id}`}><Edit3 size={14} /></button>
                <button onClick={() => remove(p.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-md" data-testid={`admin-implant-delete-${p.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Drugs ----------
function DrugsPanel() {
  const [items, setItems] = useState([]);
  const empty = {
    name: "", brand: "", category: "", indication: "", dosage: "",
    contraindications: "", key_interactions: "", clinical_notes: "", is_new: false,
  };
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/drugs"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/admin/drugs/${editing}`, form);
      else await api.post("/admin/drugs", form);
      toast.success("Saved"); setForm(empty); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };
  const edit = (it) => { setForm({ ...empty, ...it }); setEditing(it.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => { if (!window.confirm("Remove?")) return; await api.delete(`/admin/drugs/${id}`); load(); };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="module-card p-6 bg-white h-fit">
        <h3 className="font-heading text-xl font-semibold">{editing ? "Edit drug" : "Add drug"}</h3>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="admin-drug-name" placeholder="Generic name *" required value={form.name} onChange={(e) => update("name", e.target.value)} className="px-4 py-2.5 rounded-md border border-ink/15" />
            <input data-testid="admin-drug-brand" placeholder="Brand name" value={form.brand} onChange={(e) => update("brand", e.target.value)} className="px-4 py-2.5 rounded-md border border-ink/15" />
          </div>
          <input data-testid="admin-drug-category" placeholder="Category *" required value={form.category} onChange={(e) => update("category", e.target.value)} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <textarea data-testid="admin-drug-indication" placeholder="Indication" rows={2} value={form.indication} onChange={(e) => update("indication", e.target.value)} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <textarea data-testid="admin-drug-dosage" placeholder="Dosage" rows={2} value={form.dosage} onChange={(e) => update("dosage", e.target.value)} className="w-full px-4 py-2.5 rounded-md border border-ink/15 font-mono text-sm" />
          <textarea data-testid="admin-drug-contra" placeholder="Contraindications" rows={2} value={form.contraindications} onChange={(e) => update("contraindications", e.target.value)} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <textarea data-testid="admin-drug-interactions" placeholder="Key interactions" rows={2} value={form.key_interactions} onChange={(e) => update("key_interactions", e.target.value)} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <textarea data-testid="admin-drug-notes" placeholder="Clinical notes" rows={2} value={form.clinical_notes} onChange={(e) => update("clinical_notes", e.target.value)} className="w-full px-4 py-2.5 rounded-md border border-ink/15" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="admin-drug-isnew" checked={!!form.is_new} onChange={(e) => update("is_new", e.target.checked)} className="rounded border-ink/30" />
            Mark as <span className="font-medium">NEW 2025</span> (e.g., recently FDA-approved)
          </label>
          <div className="flex gap-2 pt-1">
            <button type="submit" data-testid="admin-drug-save" className="px-5 py-2.5 rounded-md text-white font-medium" style={{ backgroundColor: "#1D9E75" }}>{editing ? "Update" : "Add"}</button>
            {editing && <button type="button" onClick={() => { setEditing(null); setForm(empty); }} className="px-5 py-2.5 rounded-md border border-ink/15">Cancel</button>}
          </div>
        </form>
      </div>
      <div>
        <div className="uppercase-label">Drug list ({items.length})</div>
        <div className="mt-3 space-y-2 max-h-[80vh] overflow-y-auto pr-2">
          {items.map((p) => (
            <div key={p.id} className="module-card p-4 bg-white flex justify-between items-start gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {p.name}
                  {p.is_new && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full text-white" style={{ backgroundColor: "#1D9E75" }}>NEW</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{p.category}{p.brand ? ` · ${p.brand}` : ""}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => edit(p)} className="p-2 hover:bg-bone rounded-md" data-testid={`admin-drug-edit-${p.id}`}><Edit3 size={14} /></button>
                <button onClick={() => remove(p.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-md" data-testid={`admin-drug-delete-${p.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
